import React, { useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  CircularProgress,
  Alert,
  Pagination,
  Divider,
  Chip,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  TextField,
  InputAdornment,
} from '@mui/material';
import { Edit, Delete, Add as AddIcon, Search as SearchIcon } from '@mui/icons-material';
import { useArticleManagement } from '../hooks/useArticleManagement';
import ArticleForm from './ArticleForm';
import { useIssues } from '../hooks/useIssues';

const ITEMS_PER_PAGE = 6;

export const ArticlesTab: React.FC = () => {
  const {
    articles,
    articlesLoading,
    articlesError,
    articleFormOpen,
    formMode,
    selectedArticle,
    setArticleFormOpen,
    handleAddArticle,
    handleEditArticle,
    handleDeleteArticle,
    handleArticleSubmit,
  } = useArticleManagement();

  const { issues } = useIssues();
  const [page, setPage] = useState(1);
  const [selectedIssue, setSelectedIssue] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState('');

  const filteredArticles = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();

    return articles.filter((article) => {
      if (selectedIssue !== 'all' && article.issue?.id.toString() !== selectedIssue) {
        return false;
      }
      if (!query) return true;

      const authors = article.authors
        ?.map((a) => `${a.lastName} ${a.firstName}${a.middleName ? ` ${a.middleName}` : ''}`)
        .join(' ')
        .toLowerCase() || '';

      return (
        article.title?.toLowerCase().includes(query) ||
        article.doi?.toLowerCase().includes(query) ||
        article.status?.toLowerCase().includes(query) ||
        authors.includes(query)
      );
    });
  }, [articles, selectedIssue, searchQuery]);

  const paginatedArticles = filteredArticles.slice(
    (page - 1) * ITEMS_PER_PAGE,
    page * ITEMS_PER_PAGE
  );

  const handlePageChange = (event: React.ChangeEvent<unknown>, value: number) => {
    setPage(value);
  };

  const handleIssueChange = (event: SelectChangeEvent) => {
    setSelectedIssue(event.target.value);
    setPage(1);
  };

  const getStatusChip = (status: string | undefined) => {
    if (!status) return <Chip label="Неизвестно" />;

    switch (status) {
      case 'draft':
        return <Chip label="Черновик" color="default" />;
      case 'submitted':
        return <Chip label="Отправлено" color="warning" />;
      case 'under_review':
        return <Chip label="На рецензировании" color="info" />;
      case 'rejected':
        return <Chip label="Отклонено" color="error" />;
      case 'accepted':
        return <Chip label="Принято" color="success" />;
      case 'published':
        return <Chip label="Опубликовано" color="success" />;
      default:
        return <Chip label={status} />;
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        gap: 2,
        flexWrap: 'wrap',
        mb: 3,
        pb: 2,
        borderBottom: '1px solid',
        borderColor: 'divider'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', flex: 1 }}>
          <TextField
            size="small"
            placeholder="Поиск по названию, авторам, DOI…"
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              setPage(1);
            }}
            sx={{ minWidth: 260, flex: 1, maxWidth: 420 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" color="action" />
                </InputAdornment>
              ),
            }}
          />
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Фильтр по выпуску</InputLabel>
            <Select
              value={selectedIssue}
              label="Фильтр по выпуску"
              onChange={handleIssueChange}
            >
              <MenuItem value="all">Все выпуски</MenuItem>
              {issues.map((issue) => (
                <MenuItem key={issue.id} value={issue.id}>
                  {issue.title}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
        <Button 
          variant="contained" 
          onClick={handleAddArticle}
          startIcon={<AddIcon />}
          sx={{ 
            borderRadius: 2,
            textTransform: 'none',
            px: 3
          }}
        >
          Добавить статью
        </Button>
      </Box>

      {articlesLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : articlesError ? (
        <Alert severity="error" sx={{ mb: 2 }}>{articlesError}</Alert>
      ) : (
        <>
          <Paper elevation={0} sx={{ 
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 2,
            overflow: 'hidden'
          }}>
            <List sx={{ p: 0 }}>
              {paginatedArticles.length === 0 ? (
                <ListItem>
                  <ListItemText primary="Ничего не найдено" />
                </ListItem>
              ) : (
                paginatedArticles.map((article) => (
                  <React.Fragment key={article.id}>
                    <ListItem
                      sx={{
                        py: 2,
                        px: 3,
                        '&:hover': {
                          backgroundColor: 'action.hover'
                        }
                      }}
                    >
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                            <Typography component="div" variant="subtitle1" sx={{ fontWeight: 500 }}>
                              {article.title}
                            </Typography>
                            {getStatusChip(article.status)}
                          </Box>
                        }
                        secondary={
                          <Box component="div" sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                            <Box component="div" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Typography component="div" variant="body2" color="text.secondary">
                                Авторы:
                              </Typography>
                              <Typography component="div" variant="body2">
                                {article.authors?.map(a => `${a.lastName} ${a.firstName}${a.middleName ? ` ${a.middleName}` : ''}`).join(', ')}
                              </Typography>
                            </Box>
                            <Box component="div" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Typography component="div" variant="body2" color="text.secondary">
                                DOI:
                              </Typography>
                              <Typography component="div" variant="body2">
                                {article.doi || 'Не указан'}
                              </Typography>
                            </Box>
                          </Box>
                        }
                      />
                      <ListItemSecondaryAction>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <IconButton 
                            edge="end" 
                            onClick={() => handleEditArticle(article)}
                            sx={{ 
                              color: 'primary.main',
                              '&:hover': { backgroundColor: 'primary.lighter' }
                            }}
                          >
                            <Edit />
                          </IconButton>
                          <IconButton 
                            edge="end" 
                            onClick={() => handleDeleteArticle(article.id)}
                            sx={{ 
                              color: 'error.main',
                              '&:hover': { backgroundColor: 'error.lighter' }
                            }}
                          >
                            <Delete />
                          </IconButton>
                        </Box>
                      </ListItemSecondaryAction>
                    </ListItem>
                    <Divider />
                  </React.Fragment>
                ))
              )}
            </List>
          </Paper>

          {filteredArticles.length > ITEMS_PER_PAGE && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
              <Pagination
                count={Math.ceil(filteredArticles.length / ITEMS_PER_PAGE)}
                page={page}
                onChange={handlePageChange}
                color="primary"
                shape="rounded"
                sx={{
                  '& .MuiPaginationItem-root': {
                    borderRadius: 1
                  }
                }}
              />
            </Box>
          )}
        </>
      )}

      {articleFormOpen && (
        <ArticleForm
          open={articleFormOpen}
          onClose={() => setArticleFormOpen(false)}
          onSubmit={handleArticleSubmit}
          initialData={selectedArticle || undefined}
          mode={formMode}
        />
      )}
    </Box>
  );
}; 